import type {
  BrandInvoiceMode,
  CreditReceiptOrderResponse,
  DownloadFinanceAttachmentResponse,
  GetReceiptOrderResponse,
  IssueReceiptInvoiceResponse,
  ListBrandInvoiceSettingsResponse,
  ListFinanceAttachmentsResponse,
  ListReceiptOrdersResponse,
  ReceiptChannel,
  ReceiptCurrency,
  ReceiptInvoiceStatus,
  ReceiptOrderStatus,
  RejectReceiptOrderResponse,
  SubmitReceiptOrderResponse,
  UpdateBrandInvoiceSettingsResponse,
  UploadFinanceAttachmentResponse,
  VoidReceiptInvoiceResponse,
  VoidReceiptOrderResponse,
} from '@nadoutong/contracts';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * Typed client + React Query hooks for `/v1/ops/receipts/*` — the manual
 * receipt console surface (epic #1031 E2-R5, #1063) on top of the E2-R4
 * BFF (#1062).
 *
 * Server-side scoping is the contract: CM sessions are row-scoped to
 * their own submissions, leads to their brand book, finance-manager and
 * admin see everything — the client only sends NARROWING filters
 * (status / brand / org / entered_by), never scope-widening parameters,
 * and amounts are only ever operator-collected form values (the credit
 * request carries no amounts at all; credited == verified at policy
 * rate is server-enforced).
 *
 * All queries share the `['receipts']` root key so a decision mutation
 * invalidates the queue slice, the full list, and any open detail
 * together — including after a LOST decide race (409
 * `RECEIPT_ORDER_NOT_SUBMITTED`), where the refetch is what surfaces
 * the true decided state (#1063 AC 4).
 */

const BASE_PATH = '/v1/ops/receipts';

export const RECEIPTS_ROOT_KEY = ['receipts'] as const;

/** 30s polling for the finance queue, matching the approvals precedent. */
export const RECEIPTS_POLL_INTERVAL_MS = 30_000;

export interface ReceiptListScope {
  readonly limit: number;
  readonly offset: number;
  /** '' = all statuses. */
  readonly status: ReceiptOrderStatus | '';
  /** '' = all brands (only meaningful for finance/admin sessions). */
  readonly brand: string;
  /** '' = all customers. */
  readonly orgId: string;
  /** '' = no company search; otherwise a CRM company-name fuzzy match. */
  readonly company: string;
  /** '' = all invoice states; 'open' = the finance invoices-to-issue worklist. */
  readonly invoiceStatus: ReceiptInvoiceStatus | '';
}

function listPath(scope: ReceiptListScope): string {
  const params = new URLSearchParams();
  params.set('limit', String(scope.limit));
  params.set('offset', String(scope.offset));
  if (scope.status !== '') {
    params.set('status', scope.status);
  }
  if (scope.brand.length > 0) {
    params.set('brand', scope.brand);
  }
  if (scope.orgId.length > 0) {
    params.set('org_id', scope.orgId);
  }
  if (scope.company.length > 0) {
    params.set('company', scope.company);
  }
  if (scope.invoiceStatus !== '') {
    params.set('invoice_status', scope.invoiceStatus);
  }
  return `${BASE_PATH}?${params.toString()}`;
}

export function receiptListQueryKey(
  scope: ReceiptListScope,
): readonly ['receipts', 'list', ReceiptListScope] {
  return ['receipts', 'list', scope] as const;
}

export function receiptDetailQueryKey(
  receiptId: string,
): readonly ['receipts', 'detail', string] {
  return ['receipts', 'detail', receiptId] as const;
}

export function receiptAttachmentsQueryKey(
  receiptId: string,
): readonly ['receipts', 'attachments', string] {
  return ['receipts', 'attachments', receiptId] as const;
}

export async function listReceipts(
  scope: ReceiptListScope,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListReceiptOrdersResponse> {
  return opsApiRequest<ListReceiptOrdersResponse>(listPath(scope), { signal }, fetchImpl);
}

/**
 * Scoped receipt list. `poll` turns on the 30s refetch used by the
 * finance queue slice (a receipt submitted elsewhere appears within one
 * cycle); the browse list leaves it off.
 */
export function useReceiptList(
  scope: ReceiptListScope,
  options: { readonly enabled: boolean; readonly poll?: boolean },
): UseQueryResult<ListReceiptOrdersResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: receiptListQueryKey(scope),
    queryFn: ({ signal }) => listReceipts(scope, fetchImpl, signal),
    enabled: options.enabled,
    refetchInterval: options.poll === true ? RECEIPTS_POLL_INTERVAL_MS : false,
  });
}

/** Receipt + full transition history (server-scoped; 403 outside scope). */
export function useReceiptDetail(
  receiptId: string | null,
): UseQueryResult<GetReceiptOrderResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: receiptDetailQueryKey(receiptId ?? ''),
    queryFn: ({ signal }) =>
      opsApiRequest<GetReceiptOrderResponse>(
        `${BASE_PATH}/${encodeURIComponent(receiptId ?? '')}`,
        { signal },
        fetchImpl,
      ),
    enabled: receiptId !== null,
  });
}

// ─── sales submit ────────────────────────────────────────────────────────

/**
 * Operator-collected receipt facts. `client_ref` is the submit
 * idempotency key — generated once per dialog open so a retry after a
 * network error replays instead of double-inserting. `account_id`,
 * `entered_by`, and `brand` are pinned server-side and never sent.
 */
export interface SubmitReceiptInput {
  readonly orgId: string;
  readonly clientRef: string;
  readonly amountMinor: number;
  readonly currency: ReceiptCurrency;
  readonly channel: ReceiptChannel;
  readonly actualFeeMinor: number;
  readonly receivedAt: string;
  readonly contractRef: string | null;
  readonly note: string | null;
  /**
   * Invoice need + tax point, sales-confirmed at submit (#1064).
   * Validated server-side against the brand's invoice settings; the
   * dialog only collects what the brand mode allows.
   */
  readonly needInvoice: boolean;
  readonly taxPointBps: number | null;
  readonly invoiceTitle: string | null;
  readonly taxNo: string | null;
}

export function useSubmitReceipt(): UseMutationResult<
  SubmitReceiptOrderResponse,
  Error,
  SubmitReceiptInput
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      opsApiRequest<SubmitReceiptOrderResponse>(
        BASE_PATH,
        {
          method: 'POST',
          body: {
            client_ref: input.clientRef,
            org_id: input.orgId,
            amount_minor: input.amountMinor,
            currency: input.currency,
            channel: input.channel,
            actual_fee_minor: input.actualFeeMinor,
            received_at: input.receivedAt,
            contract_ref: input.contractRef,
            note: input.note,
            need_invoice: input.needInvoice,
            tax_point_bps: input.taxPointBps,
            invoice_title: input.invoiceTitle,
            tax_no: input.taxNo,
          },
        },
        fetchImpl,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: RECEIPTS_ROOT_KEY });
    },
  });
}

// ─── finance decisions ───────────────────────────────────────────────────

export type ReceiptDecisionAction = 'credit' | 'reject' | 'void';

export type ReceiptDecisionResponse =
  | CreditReceiptOrderResponse
  | RejectReceiptOrderResponse
  | VoidReceiptOrderResponse;

export interface ReceiptDecisionInput {
  readonly id: string;
  readonly action: ReceiptDecisionAction;
  readonly note: string | null;
}

/**
 * One mutation for the three finance decisions. Invalidation runs
 * `onSettled` (not only on success) so LOSING the decide race — 409
 * `RECEIPT_ORDER_NOT_SUBMITTED` — also refreshes the queue, the list,
 * and the open detail to the true decided state (#1063 AC 4).
 */
export function useReceiptDecision(): UseMutationResult<
  ReceiptDecisionResponse,
  Error,
  ReceiptDecisionInput
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, note }) =>
      opsApiRequest<ReceiptDecisionResponse>(
        `${BASE_PATH}/${encodeURIComponent(id)}/${action}`,
        { method: 'POST', body: { note } },
        fetchImpl,
      ),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: RECEIPTS_ROOT_KEY });
    },
  });
}

// ─── invoice actions (finance issue / void, #1064) ──────────────────────

export type ReceiptInvoiceAction = 'issue' | 'void';

export type ReceiptInvoiceActionResponse =
  | IssueReceiptInvoiceResponse
  | VoidReceiptInvoiceResponse;

export interface ReceiptInvoiceActionInput {
  readonly id: string;
  readonly action: ReceiptInvoiceAction;
  readonly note: string | null;
}

/**
 * One mutation for both finance invoice decisions (`open → issued |
 * voided`). Invalidation runs `onSettled` — losing the decide race
 * (409 `RECEIPT_INVOICE_NOT_OPEN`) also refreshes list + detail to the
 * true invoice state, matching the receipt-decision convention.
 */
export function useReceiptInvoiceAction(): UseMutationResult<
  ReceiptInvoiceActionResponse,
  Error,
  ReceiptInvoiceActionInput
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, note }) =>
      opsApiRequest<ReceiptInvoiceActionResponse>(
        `${BASE_PATH}/${encodeURIComponent(id)}/invoice/${action}`,
        { method: 'POST', body: { note } },
        fetchImpl,
      ),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: RECEIPTS_ROOT_KEY });
    },
  });
}

// ─── per-brand invoice settings (#1064) ─────────────────────────────────

const INVOICE_SETTINGS_PATH = '/v1/ops/receipt-invoice-settings';

export const BRAND_INVOICE_SETTINGS_KEY = ['brand-invoice-settings'] as const;

/**
 * Per-brand invoice configuration (DB-config, admin-edited). Read-open
 * to receipt viewers: the submit dialog resolves the pinned customer's
 * brand mode from this list to decide which invoice fields to render.
 */
export function useBrandInvoiceSettings(options?: {
  readonly enabled?: boolean;
}): UseQueryResult<ListBrandInvoiceSettingsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: BRAND_INVOICE_SETTINGS_KEY,
    queryFn: ({ signal }) =>
      opsApiRequest<ListBrandInvoiceSettingsResponse>(
        INVOICE_SETTINGS_PATH,
        { signal },
        fetchImpl,
      ),
    enabled: options?.enabled ?? true,
  });
}

export interface UpdateBrandInvoiceSettingsInput {
  readonly brand: string;
  readonly mode: BrandInvoiceMode;
  readonly defaultTaxPointBps: number | null;
  readonly maxTaxPointBps: number;
}

export function useUpdateBrandInvoiceSettings(): UseMutationResult<
  UpdateBrandInvoiceSettingsResponse,
  Error,
  UpdateBrandInvoiceSettingsInput
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) =>
      opsApiRequest<UpdateBrandInvoiceSettingsResponse>(
        `${INVOICE_SETTINGS_PATH}/${encodeURIComponent(input.brand)}`,
        {
          method: 'PUT',
          body: {
            mode: input.mode,
            default_tax_point_bps: input.defaultTaxPointBps,
            max_tax_point_bps: input.maxTaxPointBps,
          },
        },
        fetchImpl,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: BRAND_INVOICE_SETTINGS_KEY });
    },
  });
}

// ─── evidence attachments ────────────────────────────────────────────────

/**
 * Plain list fetch — also used imperatively by the submit dialog's
 * attachment-retry mode to detect an upload that landed server-side
 * even though its response was lost (#1098).
 */
export async function listReceiptAttachments(
  receiptId: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListFinanceAttachmentsResponse> {
  return opsApiRequest<ListFinanceAttachmentsResponse>(
    `${BASE_PATH}/${encodeURIComponent(receiptId)}/attachments`,
    { signal },
    fetchImpl,
  );
}

export function useReceiptAttachments(
  receiptId: string | null,
): UseQueryResult<ListFinanceAttachmentsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: receiptAttachmentsQueryKey(receiptId ?? ''),
    queryFn: ({ signal }) => listReceiptAttachments(receiptId ?? '', fetchImpl, signal),
    enabled: receiptId !== null,
  });
}

export interface UploadReceiptAttachmentInput {
  readonly receiptId: string;
  readonly fileName: string;
  readonly contentType: string;
  readonly dataBase64: string;
}

export async function uploadReceiptAttachment(
  input: UploadReceiptAttachmentInput,
  fetchImpl: typeof fetch,
): Promise<UploadFinanceAttachmentResponse> {
  return opsApiRequest<UploadFinanceAttachmentResponse>(
    `${BASE_PATH}/${encodeURIComponent(input.receiptId)}/attachments`,
    {
      method: 'POST',
      body: {
        file_name: input.fileName,
        content_type: input.contentType,
        data_base64: input.dataBase64,
      },
    },
    fetchImpl,
  );
}

export function useUploadReceiptAttachment(): UseMutationResult<
  UploadFinanceAttachmentResponse,
  Error,
  UploadReceiptAttachmentInput
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => uploadReceiptAttachment(input, fetchImpl),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: receiptAttachmentsQueryKey(input.receiptId) });
    },
  });
}

/**
 * Download one attachment (base64 JSON — no presigned URLs in v1).
 * Plain async function: callers trigger it from a click handler and
 * hand the decoded bytes to the browser as an object-URL download.
 */
export async function downloadReceiptAttachment(
  receiptId: string,
  attachmentId: string,
  fetchImpl: typeof fetch,
): Promise<DownloadFinanceAttachmentResponse> {
  return opsApiRequest<DownloadFinanceAttachmentResponse>(
    `${BASE_PATH}/${encodeURIComponent(receiptId)}/attachments/${encodeURIComponent(attachmentId)}`,
    {},
    fetchImpl,
  );
}
